// The address a hall screen opens, and nothing else.
//
// THE TOKEN IS IN THE FRAGMENT. screenSourceFromLocation in the player reads
// `#activation=<id>&token=<token>`. A fragment is never sent to a server, never
// reaches a Referer header, and never lands in a venue proxy's access log. A
// query string does all three. This file is the one place the console writes
// that address, so a second builder cannot quietly put the token after `?`.

/**
 * The player page for one activation and one view token.
 *
 * `origin` is this build's named kiosk host, never a guessed one. The token is
 * written after `#` and encoded the same way the player decodes it
 * (`URLSearchParams`), so a token that happens to contain `&` still round-trips.
 */
export function screenLink(origin: string, activationId: string, token: string): string {
  const host = origin.endsWith("/") ? origin.slice(0, -1) : origin;
  const params = new URLSearchParams({ activation: activationId, token });
  return `${host}/#${params.toString()}`;
}

/**
 * The same screen address, pinned to one piece.
 *
 * `item` stays in the fragment with the token. A query string would put the
 * token in every venue proxy log.
 */
export function screenItemLink(screenHref: string, itemId: string): string {
  const trimmed = itemId.trim();
  if (trimmed === "") return screenHref;
  const hashAt = screenHref.indexOf("#");
  if (hashAt < 0) return screenHref;
  const host = screenHref.slice(0, hashAt);
  const found = new URLSearchParams(screenHref.slice(hashAt + 1));
  if ((found.get("activation") ?? "") === "" || (found.get("token") ?? "") === "") return screenHref;
  found.set("item", trimmed);
  return `${host}#${found.toString()}`;
}

/** True when the fragment carries both an activation and a screen token. */
export function screenHrefLooksComplete(href: string): boolean {
  const hashAt = href.indexOf("#");
  if (hashAt < 0) return false;
  const found = new URLSearchParams(href.slice(hashAt + 1));
  return (found.get("activation") ?? "").trim() !== "" && (found.get("token") ?? "").trim() !== "";
}
